"use client";

import { formatCanvasFuse, type CanvasFuseLabel } from "./canvas-fuse-label";
import {
  Handle,
  Position,
  useReactFlow,
  ViewportPortal,
} from "@xyflow/react";
import { useEffect, useRef, useState } from "react";
import { getDefinition } from "@/lib/catalog";
import { terminalDisplayColor, terminalDisplayPolarity } from "@/lib/terminal-display";
import { useSystemStore } from "@/lib/store";
import { useCanvasViewOnly } from "./canvas-mode";
import { busbarRodPlacement, nearestSidePlacement } from "@/lib/terminal-snap";
import type { Terminal } from "@/lib/types";
import { formatAmp } from "@/lib/utils";
import { useModKey, useShiftKey } from "@/lib/use-shift-key";
import { KeystrokeHint, MOD_LABEL } from "@/components/ui/keystroke-hint";
import { useIsTouchLayout } from "@/lib/use-is-touch-layout";
import { terminalTangentHitCaps } from "@/lib/canvas-interaction";
import { useCanvasDrag } from "@/lib/canvas-drag";
import { bindPointerDrag } from "./pointer-drag";

const sideToPosition = (side: Terminal["side"]): Position =>
  ({
    left: Position.Left,
    right: Position.Right,
    top: Position.Top,
    bottom: Position.Bottom,
  })[side];

type TouchHitCapStyle = React.CSSProperties & {
  [key: `--touch-hit-${string}`]: string | undefined;
};

function TerminalDot({
  nodeId,
  terminal,
  fuse,
  compact = false,
  hollow = false,
  labelVisible,
  nodeAbsX,
  nodeAbsY,
  nodeWidth,
  nodeHeight,
  touchHitBefore,
  touchHitAfter,
}: {
  nodeId: string;
  terminal: Terminal;
  fuse?: CanvasFuseLabel;
  compact?: boolean;
  hollow?: boolean;
  labelVisible: boolean;
  nodeAbsX: number;
  nodeAbsY: number;
  nodeWidth: number;
  nodeHeight: number;
  touchHitBefore?: number;
  touchHitAfter?: number;
}) {
  const { screenToFlowPosition } = useReactFlow();
  const handleRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanupRef.current?.(), []);
  const [hovered, setHovered] = useState(false);
  const modHeld = useModKey();
  const shiftHeld = useShiftKey();
  const viewOnly = useCanvasViewOnly();
  const isTouchLayout = useIsTouchLayout();
  const hasCable = useSystemStore((s) =>
    s.system.cables.some(
      (c) =>
        (c.fromNodeId === nodeId && c.fromTerminalId === terminal.id) ||
        (c.toNodeId === nodeId && c.toTerminalId === terminal.id),
    ),
  );
  const definition = useSystemStore((s) => {
    const node = s.system.nodes.find((n) => n.id === nodeId);
    return node?.kind === "component" ? getDefinition(node.definitionId, node.customDefinition) : undefined;
  });
  // Symbol terminals stay attached to their glyph. Dragging them should always
  // start a cable, including when another cable is already attached.
  const anchored = definition?.renderVariant === "switch" ||
    definition?.renderVariant === "fuse-holder" ||
    definition?.renderVariant === "chassis-ground";
  const canMoveTerminal = hasCable && !anchored && !isTouchLayout;

  // React Flow hooks mousedown/touchstart, not pointer events, so the slide
  // gesture must suppress those too or the drag opens the "New cable" modal.
  useEffect(() => {
    const el = handleRef.current;
    if (!el) return;
    if (viewOnly || !canMoveTerminal) return;
    const shouldHandle = (e: { metaKey: boolean; ctrlKey: boolean }) =>
      !e.metaKey && !e.ctrlKey;
    const onSuppress = (e: MouseEvent | TouchEvent) => {
      const meta = "metaKey" in e ? e.metaKey : false;
      const ctrl = "ctrlKey" in e ? e.ctrlKey : false;
      if (!shouldHandle({ metaKey: meta, ctrlKey: ctrl })) return;
      e.stopPropagation();
      e.preventDefault();
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey) return;

      e.stopPropagation();
      e.preventDefault();

      const store = useSystemStore.getState();
      const node = store.system.nodes.find((n) => n.id === nodeId);
      if (!node || node.kind !== "component") return;
      const def = getDefinition(node.definitionId, node.customDefinition);
      if (!def) return;

      if (e.shiftKey) {
        store.clearTerminalOverride(nodeId, terminal.id);
        // clearTerminalOverride already pushed history; fold the re-tidy in.
        store.tidyCableLayoutsForNodes([nodeId], { skipHistory: true });
        return;
      }

      dragCleanupRef.current?.();

      const onMove = (ev: PointerEvent) => {
        const flow = screenToFlowPosition(
          { x: ev.clientX, y: ev.clientY },
          { snapToGrid: false },
        );
        const cur = useSystemStore.getState();
        const n2 = cur.system.nodes.find((n) => n.id === nodeId);
        if (!n2 || n2.kind !== "component") return;
        if (def.renderVariant === "busbar") {
          // Offsets are in the catalog frame; effectiveTerminals applies rotation later.
          const placement = busbarRodPlacement(n2, def, cur.system.nodes, flow, terminal);
          cur.setTerminalOverride(nodeId, terminal.id, {
            ...placement,
            pinned: true,
          });
          return;
        }
        const { side, offset } = nearestSidePlacement(n2, def, cur.system.nodes, flow);
        cur.setTerminalOverride(nodeId, terminal.id, { side, offset, pinned: true });
      };
      dragCleanupRef.current = bindPointerDrag({
        event: e,
        target: el,
        onStart: () => {
          useSystemStore.getState().pushHistory();
          useCanvasDrag.getState().setIsDragging(true);
        },
        onMove,
        onFinish: (_event, { started }) => {
          dragCleanupRef.current = null;
          if (!started) return;
          // Geometry never changes cable demand; resume graph work once the
          // final terminal placement and its routes have been committed.
          useSystemStore.getState().tidyCableLayoutsForNodes([nodeId], {
            skipHistory: true,
          });
          useCanvasDrag.getState().setIsDragging(false);
        },
      });
    };
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("mousedown", onSuppress);
    el.addEventListener("touchstart", onSuppress, { passive: false });
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("mousedown", onSuppress);
      el.removeEventListener("touchstart", onSuppress);
    };
  }, [
    nodeId,
    terminal,
    screenToFlowPosition,
    canMoveTerminal,
    viewOnly,
  ]);

  const styleMap: Record<Terminal["side"], React.CSSProperties> = {
    left: { top: `${terminal.offset * 100}%`, left: 0 },
    right: { top: `${terminal.offset * 100}%`, right: 0 },
    top: { left: `${terminal.offset * 100}%`, top: 0 },
    bottom: { left: `${terminal.offset * 100}%`, bottom: 0 },
  };
  const touchHitCapStyle: TouchHitCapStyle =
    terminal.side === "left" || terminal.side === "right"
      ? {
          "--touch-hit-top-cap":
            touchHitBefore == null ? undefined : `${touchHitBefore}px`,
          "--touch-hit-bottom-cap":
            touchHitAfter == null ? undefined : `${touchHitAfter}px`,
        }
      : {
          "--touch-hit-left-cap":
            touchHitBefore == null ? undefined : `${touchHitBefore}px`,
          "--touch-hit-right-cap":
            touchHitAfter == null ? undefined : `${touchHitAfter}px`,
        };

  const LABEL_GAP = 6;
  const termX =
    terminal.side === "left"
      ? nodeAbsX
      : terminal.side === "right"
        ? nodeAbsX + nodeWidth
        : nodeAbsX + terminal.offset * nodeWidth;
  const termY =
    terminal.side === "top"
      ? nodeAbsY
      : terminal.side === "bottom"
        ? nodeAbsY + nodeHeight
        : nodeAbsY + terminal.offset * nodeHeight;
  const labelAnchor: { x: number; y: number; transform: string } = {
    left:   { x: termX - LABEL_GAP, y: termY, transform: "translate(-100%, -50%)" },
    right:  { x: termX + LABEL_GAP, y: termY, transform: "translate(0, -50%)" },
    top:    { x: termX, y: termY - LABEL_GAP, transform: "translate(-50%, -100%)" },
    bottom: { x: termX, y: termY + LABEL_GAP, transform: "translate(-50%, 0)" },
  }[terminal.side];


  const lbl = terminal.label;
  const displayPolarity = terminalDisplayPolarity(terminal);
  const isPositive = displayPolarity === "positive" && lbl.includes("+");
  const isNegative = displayPolarity === "negative" && (lbl.includes("−") || lbl.includes("-"));
  // User labels (for example "House-feed") cannot change electrical polarity.
  const dotColor = terminalDisplayColor(terminal, definition);
  const polaritySymbol = isPositive ? "+" : isNegative ? "−" : null;
  // A polarity-only label is already drawn inside the dot.
  const isPolarityOnlyLabel = lbl.trim() === "+" || lbl.trim() === "−" || lbl.trim() === "-";
  const fuseLabel = fuse ? formatCanvasFuse(fuse) : null;
  const outsideLabel = isPolarityOnlyLabel
    ? fuseLabel
    : fuseLabel ? `${terminal.label} · ${fuseLabel}` : terminal.label;

  const hintLabel = canMoveTerminal
    ? modHeld
      ? `${MOD_LABEL} + drag to add cable`
      : shiftHeld
        ? "click to reset terminal"
        : `drag to move · ${MOD_LABEL} drag to add cable`
    : "drag to add cable";
  const hintActive = canMoveTerminal && (modHeld || shiftHeld);
  const hintSide: "top" | "bottom" = terminal.side === "top" ? "bottom" : "top";

  return (
    <>
      <Handle
        ref={handleRef}
        id={terminal.id}
        type="source"
        position={sideToPosition(terminal.side)}
        // RF starts the connection gesture before the validity check, so the
        // top-level `nodesConnectable` alone lets a stray cable survive.
        isConnectable={!viewOnly}
        aria-label={`${terminal.label}${terminal.maxAmp > 0 ? `, max ${formatAmp(terminal.maxAmp)}` : ""}`}
        role={viewOnly ? undefined : "button"}
        tabIndex={viewOnly ? -1 : 0}
        style={{
          ...styleMap[terminal.side],
          ...touchHitCapStyle,
          background: hollow ? "var(--color-surface)" : dotColor,
          borderColor: dotColor === "#ffffff" ? "var(--color-fg-muted)" : dotColor,
          cursor: viewOnly
            ? "default"
            : canMoveTerminal && !modHeld ? "grab" : "crosshair",
          pointerEvents: viewOnly ? "none" : undefined,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onKeyDown={(event) => {
          if (viewOnly || (event.key !== "Enter" && event.key !== " ")) return;
          event.preventDefault();
          event.currentTarget.click();
        }}
        title={`${terminal.label}${terminal.maxAmp > 0 ? ` · max ${formatAmp(terminal.maxAmp)}` : ""}${
          fuseLabel ? ` · fused at ${fuseLabel}` : ""
        }`}
      >
        {polaritySymbol && (
          <svg
            viewBox="-5 -5 10 10"
            width="8"
            height="8"
            style={{ pointerEvents: "none", display: "block", overflow: "visible" }}
            aria-hidden
          >
            <line
              x1="-3" y1="0" x2="3" y2="0"
              stroke={hollow ? dotColor : "#fff"}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            {isPositive && (
              <line
                x1="0" y1="-3" x2="0" y2="3"
                stroke={hollow ? dotColor : "#fff"}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            )}
          </svg>
        )}
      </Handle>
      {!compact && outsideLabel && labelVisible && (
        <ViewportPortal>
          <div
            style={{
              position: "absolute",
              left: labelAnchor.x,
              top: labelAnchor.y,
              transform: labelAnchor.transform,
              pointerEvents: "none",
            }}
          >
            <span
              className="terminal-label"
              style={{
                display: "inline-block",
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: fuseLabel ? "var(--color-cat-distribution)" : "var(--color-fg)",
                whiteSpace: "nowrap",
                background: "color-mix(in srgb, var(--color-zone-bg) 86%, transparent)",
                padding: "1px 5px",
                borderRadius: "var(--radius-sm)",
              }}
            >
              {outsideLabel}
            </span>
          </div>
        </ViewportPortal>
      )}
      <KeystrokeHint
        anchorRef={handleRef}
        show={hovered && !viewOnly}
        active={hintActive}
        label={hintLabel}
        side={hintSide}
      />
    </>
  );
}

/** `activeTerminalsKey`: this node's terminals on a selected or hovered cable, comma-joined. */
export function TerminalDots({
  terms,
  nodeId,
  nodeActive,
  activeTerminalsKey,
  terminalFuses,
  compact,
  positionAbsoluteX,
  positionAbsoluteY,
  nodeWidth,
  nodeHeight,
}: {
  terms: readonly Terminal[];
  nodeId: string;
  nodeActive: boolean;
  activeTerminalsKey: string;
  terminalFuses: Record<string, CanvasFuseLabel> | undefined;
  compact: boolean;
  positionAbsoluteX: number;
  positionAbsoluteY: number;
  nodeWidth: number;
  nodeHeight: number;
}) {
  const activeSet = activeTerminalsKey
    ? new Set(activeTerminalsKey.split(","))
    : null;
  return (
    <>
      {terms.map((t) => {
        const caps = terminalTangentHitCaps(
          terms,
          t,
          nodeWidth,
          nodeHeight,
        );
        return (
          <TerminalDot
            key={t.id}
            nodeId={nodeId}
            terminal={t}
            compact={compact}
            hollow
            labelVisible={nodeActive || (activeSet?.has(t.id) ?? false)}
            fuse={terminalFuses?.[t.id]}
            nodeAbsX={positionAbsoluteX}
            nodeAbsY={positionAbsoluteY}
            nodeWidth={nodeWidth}
            nodeHeight={nodeHeight}
            touchHitBefore={caps.before}
            touchHitAfter={caps.after}
          />
        );
      })}
    </>
  );
}
